import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { toast } from "sonner";

export interface UploadFileFormProps {
  contactId: string;
  onUpload: () => void;
}

export const UploadFileForm = ({ contactId, onUpload }: UploadFileFormProps) => {
  const { t } = useTranslation("contacts");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profileField, setProfileField] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [dontShare, setDontShare] = useState(false);
  const [flagForReview, setFlagForReview] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const uploadFileMutation = useUploadTellescopeFile({
    prepareMutationOptions: {
      onError: () => {
        toast.error(t("There was an error uploading the file."));
      },
    },
    confirmMutationOptions: {
      onSuccess: () => {
        onUpload();
      },
      onError: () => {
        toast.error(t("There was an error uploading the file."));
      },
    },
  });

  const handleSubmit = async () => {
    if (!selectedFile) return;
    await uploadFileMutation.uploadFileAsync(
      {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        publicRead: false, // TODO: check if there should be logic to this
        isCalledOut: flagForReview,
        enduserId: contactId,
        hiddenFromEnduser: dontShare,
      },
      selectedFile
    );
  };

  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
      {/* File Upload and Profile Field - Two Column Layout */}
      <div className="grid grid-cols-2 gap-4">
        {/* File Upload */}
        <div className="space-y-2">
          <Label htmlFor="file-upload" className="text-sm font-medium">
            {t("Upload File")}
          </Label>
          <Input id="file-upload" type="file" onChange={handleFileChange} />
        </div>

        {/* Save to Custom Profile Field */}
        <div className="space-y-2">
          <Label htmlFor="profile-field" className="text-sm font-medium">
            {t("Save to Custom Profile Field")}
          </Label>
          <Select value={profileField} onValueChange={setProfileField}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="No" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="medical-records">{t("Medical Records")}</SelectItem>
              <SelectItem value="insurance-documents">{t("Insurance Documents")}</SelectItem>
              <SelectItem value="lab-results">{t("Lab Results")}</SelectItem>
              <SelectItem value="imaging">{t("Imaging")}</SelectItem>
              <SelectItem value="prescriptions">{t("Prescriptions")}</SelectItem>
              <SelectItem value="other">{t("Other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Selected File Info */}
      {selectedFile && (
        <p className="text-sm text-muted-foreground">
          {`${t("Selected:")} ${selectedFile.name} ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
        </p>
      )}

      {/* Checkboxes */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox id="send-email" checked={sendEmail} onCheckedChange={(checked) => setSendEmail(checked === true)} />
          <Label htmlFor="send-email" className="text-sm">
            {t("Send email notification")}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox id="dont-share" checked={dontShare} onCheckedChange={(checked) => setDontShare(checked === true)} />
          <Label htmlFor="dont-share" className="text-sm">
            {t("Don")}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="flag-review"
            checked={flagForReview}
            onCheckedChange={(checked) => setFlagForReview(checked === true)}
          />
          <Label htmlFor="flag-review" className="text-sm">
            {t("Flag for review")}
          </Label>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSubmit} className="w-full md:w-auto" disabled={!selectedFile}>
          {t("Upload File")}
        </Button>
      </div>
    </div>
  );
};
